import React, { useState, useEffect, useRef } from "react";
import { Form, Card, Button } from "react-bootstrap";
import { useLocation } from "react-router-dom";
import CountryCombobox from "./CountryCombobox";
import { getProductTransportCountryDropdown } from "../../../../../apis/Dropdown";
import { getProductDetail, updateProduct } from "../../../../../apis/Product";
import { updateProductEpdApproval, getProductLcaVersionsCurrent } from "../../../../../apis/Product/ProductEPDApi";
import EPDApprovalSection from "./EPDApprovalSection";
import "./ProductHeader.css";

// Map backend EPD approval (snake_case) to frontend (camelCase)
const epdApprovalFromBackend = (raw) => {
  if (!raw) return null;
  return {
    declarationNumber: raw.declaration_number ?? "",
    programmeOperator: raw.programme_operator ?? "",
    declarationOwner: raw.declaration_owner ?? "",
    issueDate: raw.issue_date ?? "",
    validTo: raw.valid_to ?? "",
    verifiedBy: raw.verified_by ?? "",
    verificationDate: raw.verification_date ?? "",
  };
};
const epdApprovalToBackend = (data) => ({
  declaration_number: data.declarationNumber ?? "",
  programme_operator: data.programmeOperator ?? "",
  declaration_owner: data.declarationOwner ?? "",
  issue_date: data.issueDate ?? "",
  valid_to: data.validTo ?? "",
  verified_by: data.verifiedBy ?? "",
  verification_date: data.verificationDate ?? "",
});

const certificateOptions = [
  { value: "gots", label: "GOTS" },
  { value: "oeko", label: "Oeko-Tex" },
  { value: "fsc", label: "FSC" },
  { value: "fairtrade", label: "Fairtrade" },
  { value: "cradle", label: "Cradle to Cradle" },
];

const ProductHeader = ({ data, setData, productId, onLockStatusLoad, setDppData, lockRefreshTrigger, lcaLockStatus = "unlocked", onEpdApprovalSaved }) => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const businessId = queryParams.get("business") || localStorage.getItem("id_business");
  
  const [countryOptions, setCountryOptions] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const saveTimeoutRef = useRef(null);
  const currentProductDataRef = useRef(null); // Store current product data from backend

  // Load country dropdown options
  useEffect(() => {
    getProductTransportCountryDropdown({
      setDropdown: setCountryOptions,
    });
  }, []);

  // Load product details from backend
  useEffect(() => {
    if (!productId || !businessId) return;

    getProductDetail({
      id: productId,
      business: businessId,
      setObjects: (product) => {
        currentProductDataRef.current = product;
      },
      setEpdApproval: (raw) => {
        const mapped = epdApprovalFromBackend(raw);
        if (mapped) setEpdApprovalData(mapped);
      },
      setLcaLockStatus: (status) => {
        onLockStatusLoad?.(status || "unlocked");
      },
      setCountryOrigin: (country) => {
        setData(prev => ({ ...prev, manufacturingCountry: country || "" }));
      },
      setLifetimeFunctional: (lifetime) => {
        setData(prev => ({ ...prev, lifetime: lifetime?.toString() || "300" }));
      },
      setLifetimeFunctionalUnit: (unit) => {
        setData(prev => ({ ...prev, lifetimeUnit: unit || "use" }));
      },
      setSalesShare: (share) => {
        setData(prev => ({ ...prev, salesPercentage: share?.toString() || "" }));
      },
      setIndividualProduct: (weight) => {
        setData(prev => ({ ...prev, weightExcl: weight?.toString() || "750" }));
      },
      setIndividualProductPackaging: (packaging) => {
        // Calculate weight incl from packaging if needed
        setData(prev => {
          const weightExcl = parseFloat(prev?.weightExcl || 0);
          const packagingWeight = parseFloat(packaging || 0);
          return {
            ...prev, 
            weightIncl: (weightExcl + packagingWeight).toString() || "750" 
          };
        });
      },
      setFinalWeight: (weight) => {
        setData(prev => ({ ...prev, weightReading: weight?.toString() || "750" }));
      },
      setWasteWeight: (waste) => {
        setData(prev => ({ ...prev, waste: waste?.toString() || "0" }));
      },
      setTotalWeight: (total) => {
        setData(prev => ({ ...prev, totalWeight: total?.toString() || "0" }));
      },
      setWasteWeighIn: (wasteReading) => {
        setData(prev => ({ ...prev, wasteReading: wasteReading?.toString() || "0" }));
      },
      setTotalWeighIn: (totalReading) => {
        setData(prev => ({ ...prev, totalReading: totalReading?.toString() || "0" }));
      },
      setSoldToCustomer: (sold) => {
        setData(prev => ({ ...prev, soldChecked: !!sold }));
      },
      setTotalProductsSold: (amount) => {
        setData(prev => ({ ...prev, soldAmount: amount?.toString() || "" }));
      },
      setLeasedToCustomer: (leased) => {
        setData(prev => ({ ...prev, leasedChecked: !!leased }));
      },
      setTotalProductsLeased: (amount) => {
        setData(prev => ({ ...prev, leasedAmount: amount?.toString() || "" }));
      },
      setTakeBackCustomer: (takeback) => {
        setData(prev => ({ ...prev, takebackChecked: !!takeback }));
      },
      setTotalProductsTakeback: (amount) => {
        setData(prev => ({ ...prev, takebackAmount: amount?.toString() || "" }));
      },
      setFunctionalUnitNum: (amount) => {
        setData(prev => ({ ...prev, fuAmount: amount?.toString() || "1" }));
      },
      setFunctionalUnitUnit: (unit) => {
        setData(prev => ({ ...prev, fuUnit: unit || "pcs" }));
      },
      setFunctionalUnit: (unit) => {
        setData(prev => ({ ...prev, functionalUnit: unit || "" }));
      },
      setFunctionalUnitDesc: (desc) => {
        // Functional unit description might be stored here
        setData(prev => {
          if (desc && !prev?.functionalUnit) {
            return { ...prev, functionalUnit: desc || "" };
          }
          return prev;
        });
      },
      setCertificates: (certs) => {
        if (certs && certs.length > 0) {
          const formattedCerts = certs.map(cert => ({
            certificate: cert.cert_name || cert.certificate || "",
            verified: cert.verified === "yes" || cert.verified === true ? "yes" : "no",
            date: cert.date || "",
            organization: cert.organization || "",
            uid: cert.uid || "",
          }));
          setData(prev => ({ ...prev, certifications: formattedCerts }));
        }
      },
      setCcsClassification: (classification) => {
        setData(prev => ({ ...prev, ccsClassification: classification || "" }));
      },
      setGtinEan: (gtin) => {
        setData(prev => ({ ...prev, gtinEan: gtin || "" }));
      },
      setBastaNumber: (basta) => {
        setData(prev => ({ ...prev, bastaNumber: basta || "" }));
      },
      setOtherIds: (otherIds) => {
        setData(prev => ({ ...prev, otherIds: otherIds || "" }));
      },
      setCreatedModified: (date) => {
        setData(prev => ({ ...prev, createdModified: date || "" }));
      },
      setSector: (sector) => {
        setData(prev => ({ ...prev, sector: sector || "" }));
      },
      setDppData: (dpp) => {
        if (dpp) setDppData?.(dpp);
      },
    });
  }, [productId, businessId, setData, lockRefreshTrigger, onLockStatusLoad, setDppData]);

  // Save product fields to backend with debouncing
  // Only sends the fields that are being updated, preserving existing values for others
  const saveProductField = (updateData) => {
    if (!productId || !businessId) return;

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Set new timeout for debounced save
    saveTimeoutRef.current = setTimeout(() => {
      setIsSaving(true);
      
      // Get current product data from backend (stored when loaded)
      const currentProduct = currentProductDataRef.current;
      
      // Build update payload - only include fields that are being updated
      // For required fields like 'name', use current value if not being updated
      const backendData = {
        id: productId,
        business: businessId,
      };
      
      // Always include name if it exists (required field)
      // Use updated value if provided, otherwise use current value from backend
      if (updateData.name !== undefined) {
        backendData.name = updateData.name;
      } else if (currentProduct?.name) {
        backendData.name = currentProduct.name;
      }
      
      // Only include fields that are in updateData (fields being updated)
      if (updateData.countryOrigin !== undefined) backendData.countryOrigin = updateData.countryOrigin;
      if (updateData.lifetimeFunctional !== undefined) backendData.lifetimeFunctional = updateData.lifetimeFunctional;
      if (updateData.lifetimeFunctionalUnit !== undefined) backendData.lifetimeFunctionalUnit = updateData.lifetimeFunctionalUnit;
      if (updateData.salesShare !== undefined) backendData.salesShare = updateData.salesShare;
      if (updateData.individualProduct !== undefined) backendData.individualProduct = updateData.individualProduct;
      if (updateData.individualProductPackaging !== undefined) backendData.individualProductPackaging = updateData.individualProductPackaging;
      if (updateData.finalWeight !== undefined) backendData.finalWeight = updateData.finalWeight;
      if (updateData.wasteWeight !== undefined) backendData.wasteWeight = updateData.wasteWeight;
      if (updateData.totalWeight !== undefined) backendData.totalWeight = updateData.totalWeight;
      if (updateData.soldToCustomer !== undefined) backendData.soldToCustomer = updateData.soldToCustomer;
      if (updateData.totalProductsSold !== undefined) backendData.totalProductsSold = updateData.totalProductsSold;
      if (updateData.leasedToCustomer !== undefined) backendData.leasedToCustomer = updateData.leasedToCustomer;
      if (updateData.totalProductsLeased !== undefined) backendData.totalProductsLeased = updateData.totalProductsLeased;
      if (updateData.takeBackCustomer !== undefined) backendData.takeBackCustomer = updateData.takeBackCustomer;
      if (updateData.totalProductsTakeback !== undefined) backendData.totalProductsTakeback = updateData.totalProductsTakeback;
      if (updateData.functionalUnitNum !== undefined) backendData.functionalUnitNum = updateData.functionalUnitNum;
      if (updateData.functionalUnit !== undefined) backendData.functionalUnit = updateData.functionalUnit;
      if (updateData.functionalUnitDesc !== undefined) backendData.functionalUnitDesc = updateData.functionalUnitDesc;
      if (updateData.certificates !== undefined) backendData.certificates = updateData.certificates;
      if (updateData.wasteWeighIn !== undefined) backendData.wasteWeighIn = updateData.wasteWeighIn;
      if (updateData.totalWeighIn !== undefined) backendData.totalWeighIn = updateData.totalWeighIn;
      if (updateData.functionalUnitUnit !== undefined) backendData.functionalUnitUnit = updateData.functionalUnitUnit;
      if (updateData.ccsClassification !== undefined) backendData.ccsClassification = updateData.ccsClassification;
      if (updateData.gtinEan !== undefined) backendData.gtinEan = updateData.gtinEan;
      if (updateData.bastaNumber !== undefined) backendData.bastaNumber = updateData.bastaNumber;
      if (updateData.otherIds !== undefined) backendData.otherIds = updateData.otherIds;
      if (updateData.createdModified !== undefined) backendData.createdModified = updateData.createdModified;

      updateProduct({
        ...backendData,
        setErrorMessage: (error) => {
          if (error) {
            console.error("Error saving product header:", error);
          }
        },
        callback: () => {
          setIsSaving(false);
        },
      });
    }, 1000); // 1 second debounce
  };

  // Handle country change
  const handleCountryChange = (newCountry) => {
    setData({ ...data, manufacturingCountry: newCountry });
    saveProductField({ countryOrigin: newCountry });
  };

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      if (epdSaveTimeoutRef.current) clearTimeout(epdSaveTimeoutRef.current);
    };
  }, []);

  const [lcaVersionSubmitted, setLcaVersionSubmitted] = useState(false);
  const [epdApprovalData, setEpdApprovalData] = useState({
    declarationNumber: "",
    programmeOperator: "",
    declarationOwner: "",
    issueDate: "",
    validTo: "",
    verifiedBy: "",
    verificationDate: "",
  });
  const epdSaveTimeoutRef = useRef(null);

  const epdApprovalDataRef = useRef(epdApprovalData);
  epdApprovalDataRef.current = epdApprovalData;

  useEffect(() => {
    if (!productId || !businessId) return;
    getProductLcaVersionsCurrent({
      productId,
      businessId,
      onSuccess: (res) => {
        const version = res?.data ?? res;
        const status = version?.status ?? "";
        setLcaVersionSubmitted(
          status === "submitted" || status === "verified_locked"
        );
      },
      onError: () => setLcaVersionSubmitted(false),
    });
  }, [productId, businessId, lockRefreshTrigger]);

  const handleEpdApprovalChange = (field, value) => {
    setEpdApprovalData((prev) => ({ ...prev, [field]: value }));
    if (currentProductDataRef.current?.lca_lock_status === "epd_locked") return;
    if (!productId || !businessId) return;
    if (epdSaveTimeoutRef.current) clearTimeout(epdSaveTimeoutRef.current);
    epdSaveTimeoutRef.current = setTimeout(() => {
      const next = { ...epdApprovalDataRef.current, [field]: value };
      updateProductEpdApproval({
        productId,
        businessId,
        data: epdApprovalToBackend(next),
        onSuccess: () => {
          onEpdApprovalSaved?.();
        },
        onError: (err) => console.error("EPD approval save failed:", err),
      });
    }, 800);
  };

  // Initialize certifications array if not exists
  const certifications = data?.certifications || [
    { certificate: "", verified: "no", date: "", organization: "", uid: "" },
  ];

  const addCertification = () => {
    const newCerts = [
      ...certifications,
      { certificate: "", verified: "no", date: "", organization: "", uid: "" },
    ];
    setData({ ...data, certifications: newCerts });
  };

  const removeCertification = (index) => {
    const newCerts = certifications.filter((_, i) => i !== index);
    setData({ ...data, certifications: newCerts });
  };

  const updateCertification = (index, field, value) => {
    const newCerts = [...certifications];
    newCerts[index] = { ...newCerts[index], [field]: value };
    setData({ ...data, certifications: newCerts });
    // Save certificates to backend
    saveProductField({
      certificates: newCerts.map(cert => ({
        cert_name: cert.certificate || "",
        verified: cert.verified === "yes" ? "yes" : "no",
        date: cert.date || "",
        organization: cert.organization || "",
        uid: cert.uid || "",
      }))
    });
  };

  return (
    <>
      <Card className="product-header-card">
        {/* Manufacturing Location */}
        <div className="product-header-grid-4">
          <div>
            <Form.Label className="product-header-label-xs">
              Land for fremstilling/samling
            </Form.Label>
            <CountryCombobox 
              value={data?.manufacturingCountry || ""} 
              onChange={handleCountryChange} 
              options={countryOptions}
              placeholder="Vælg land..."
            />
          </div>
          <div>
            <Form.Label className="product-header-label-xs mb-1">
              Lifetime of functional unit
            </Form.Label>
            <Form.Control
              type="number"
              value={data?.lifetime || "300"}
              onChange={(e) => {
                setData({ ...data, lifetime: e.target.value });
                saveProductField({ lifetimeFunctional: parseFloat(e.target.value) || null });
              }}
              onBlur={() => {
                if (saveTimeoutRef.current) {
                  clearTimeout(saveTimeoutRef.current);
                  saveProductField({ lifetimeFunctional: parseFloat(data.lifetime) || null });
                }
              }}
              className="product-header-input-muted"
              disabled={isSaving}
            />
          </div>
          <div>
            <Form.Label className="product-header-label-xs mb-1">
              Unit
            </Form.Label>
            <Form.Select
              value={data?.lifetimeUnit || "use"}
              onChange={(e) => {
                setData({ ...data, lifetimeUnit: e.target.value });
                saveProductField({ lifetimeFunctionalUnit: e.target.value });
              }}
              className="product-header-input-muted"
              disabled={isSaving}
            >
              <option value="use">Use</option>
              <option value="year">Year</option>
              <option value="wash">Wash</option>
            </Form.Select>
          </div>
          <div>
            <Form.Label className="product-header-label-xs mb-1">
              Andel af virksomhedens salg (%)*
            </Form.Label>
            <Form.Control
              type="number"
              value={data?.salesPercentage || ""}
              onChange={(e) => {
                setData({ ...data, salesPercentage: e.target.value });
                saveProductField({ salesShare: parseFloat(e.target.value) || null });
              }}
              onBlur={() => {
                if (saveTimeoutRef.current) {
                  clearTimeout(saveTimeoutRef.current);
                  saveProductField({ salesShare: parseFloat(data.salesPercentage) || null });
                }
              }}
              className="product-header-input-muted"
              disabled={isSaving}
            />
          </div>
        </div>

        {/* Weight Collection - Three Columns */}
        <div className="product-header-weight-section">
          <div className="product-header-weight-header">
            <div></div>
            <Form.Label className="product-header-label-xs-medium">
              Samlet vægt ekskl. emballage
            </Form.Label>
            <Form.Label className="product-header-label-xs-medium">
              Jeres vægtaflæsning
            </Form.Label>
            <Form.Label className="product-header-label-xs-medium">
              Samlet vægt inkl. emballage og etiket*
            </Form.Label>
          </div>
          <div className="product-header-weight-rows">
            <div className="product-header-weight-row">
              <Form.Label className="product-header-label-xs">kg</Form.Label>
              <Form.Control
                type="number"
                value={data?.weightExcl || "750"}
                onChange={(e) => {
                  setData({ ...data, weightExcl: e.target.value });
                  saveProductField({ individualProduct: parseFloat(e.target.value) || null });
                }}
                onBlur={() => {
                  if (saveTimeoutRef.current) {
                    clearTimeout(saveTimeoutRef.current);
                    saveProductField({ individualProduct: parseFloat(data.weightExcl) || null });
                  }
                }}
                className="product-header-input-muted"
                disabled={isSaving}
              />
              <Form.Control
                type="number"
                value={data?.weightReading || "750"}
                onChange={(e) => {
                  setData({ ...data, weightReading: e.target.value });
                  saveProductField({ finalWeight: parseFloat(e.target.value) || null });
                }}
                onBlur={() => {
                  if (saveTimeoutRef.current) {
                    clearTimeout(saveTimeoutRef.current);
                    saveProductField({ finalWeight: parseFloat(data.weightReading) || null });
                  }
                }}
                className="product-header-input-muted"
                disabled={isSaving}
              />
              <Form.Control
                type="number"
                value={data?.weightIncl || "750"}
                onChange={(e) => {
                  setData({ ...data, weightIncl: e.target.value });
                  // Calculate packaging weight
                  const weightExcl = parseFloat(data.weightExcl || 0);
                  const weightIncl = parseFloat(e.target.value || 0);
                  const packaging = weightIncl - weightExcl;
                  saveProductField({ individualProductPackaging: packaging || null });
                }}
                onBlur={() => {
                  if (saveTimeoutRef.current) {
                    clearTimeout(saveTimeoutRef.current);
                    const weightExcl = parseFloat(data.weightExcl || 0);
                    const weightIncl = parseFloat(data.weightIncl || 0);
                    const packaging = weightIncl - weightExcl;
                    saveProductField({ individualProductPackaging: packaging || null });
                  }
                }}
                className="product-header-input-muted"
                disabled={isSaving}
              />
            </div>
            <div className="product-header-weight-row">
              <Form.Label className="product-header-label-xs">Waste</Form.Label>
              <Form.Control
                type="number"
                value={data?.waste || "0"}
                onChange={(e) => {
                  setData({ ...data, waste: e.target.value });
                  saveProductField({ wasteWeight: parseFloat(e.target.value) || null });
                }}
                onBlur={() => {
                  if (saveTimeoutRef.current) {
                    clearTimeout(saveTimeoutRef.current);
                    saveProductField({ wasteWeight: parseFloat(data.waste) || null });
                  }
                }}
                className="product-header-input-muted"
                disabled={isSaving}
              />
              <Form.Control
                type="number"
                value={data?.wasteReading || "0"}
                onChange={(e) => {
                  setData({ ...data, wasteReading: e.target.value });
                  saveProductField({ wasteWeighIn: parseFloat(e.target.value) || null });
                }}
                onBlur={() => {
                  if (saveTimeoutRef.current) {
                    clearTimeout(saveTimeoutRef.current);
                    saveProductField({ wasteWeighIn: parseFloat(data.wasteReading) || null });
                  }
                }}
                className="product-header-input-muted"
                disabled={isSaving}
              />
              <div></div>
            </div>
            <div className="product-header-weight-row">
              <Form.Label className="product-header-label-xs">Total</Form.Label>
              <Form.Control
                type="number"
                value={data?.totalWeight || "0"}
                onChange={(e) => {
                  setData({ ...data, totalWeight: e.target.value });
                  saveProductField({ totalWeight: parseFloat(e.target.value) || null });
                }}
                onBlur={() => {
                  if (saveTimeoutRef.current) {
                    clearTimeout(saveTimeoutRef.current);
                    saveProductField({ totalWeight: parseFloat(data.totalWeight) || null });
                  }
                }}
                className="product-header-input-muted"
                disabled={isSaving}
              />
              <Form.Control
                type="number"
                value={data?.totalReading || "0"}
                onChange={(e) => {
                  setData({ ...data, totalReading: e.target.value });
                  saveProductField({ totalWeighIn: parseFloat(e.target.value) || null });
                }}
                onBlur={() => {
                  if (saveTimeoutRef.current) {
                    clearTimeout(saveTimeoutRef.current);
                    saveProductField({ totalWeighIn: parseFloat(data.totalReading) || null });
                  }
                }}
                className="product-header-input-muted"
                disabled={isSaving}
              />
              <div></div>
            </div>
          </div>
        </div>

        {/* Sold/Leased/Take back Combined */}
        <div className="product-header-grid-4">
          <div>
            <Form.Label className="product-header-label-xs-medium">
              Bliver dette FU solgt eller leaset til jeres kunder? / Antal
              funktionelle enheder
            </Form.Label>
          </div>
          <div>
            <Form.Label
              className="product-header-label-xs mb-1"
              style={{ marginLeft: "28px" }}
            >
              Solgt
            </Form.Label>
            <div className="product-header-checkbox-input">
              <Form.Check
                type="checkbox"
                checked={data?.soldChecked || false}
                onChange={(e) => {
                  setData({ ...data, soldChecked: e.target.checked });
                  saveProductField({ soldToCustomer: e.target.checked ? true : null });
                }}
                className="product-header-checkbox"
                disabled={isSaving}
              />
              <Form.Control
                type="number"
                placeholder="Mængde"
                value={data?.soldAmount || ""}
                onChange={(e) => {
                  setData({ ...data, soldAmount: e.target.value });
                  saveProductField({ totalProductsSold: parseFloat(e.target.value) || null });
                }}
                onBlur={() => {
                  if (saveTimeoutRef.current) {
                    clearTimeout(saveTimeoutRef.current);
                    saveProductField({ totalProductsSold: parseFloat(data.soldAmount) || null });
                  }
                }}
                className="product-header-input-muted product-header-input-flex"
                disabled={isSaving}
              />
            </div>
          </div>
          <div>
            <Form.Label
              className="product-header-label-xs mb-1"
              style={{ marginLeft: "28px" }}
            >
              Leased
            </Form.Label>
            <div className="product-header-checkbox-input">
              <Form.Check
                type="checkbox"
                checked={data?.leasedChecked || false}
                onChange={(e) => {
                  setData({ ...data, leasedChecked: e.target.checked });
                  saveProductField({ leasedToCustomer: e.target.checked ? true : null });
                }}
                className="product-header-checkbox"
                disabled={isSaving}
              />
              <Form.Control
                type="number"
                placeholder="Mængde"
                value={data?.leasedAmount || ""}
                onChange={(e) => {
                  setData({ ...data, leasedAmount: e.target.value });
                  saveProductField({ totalProductsLeased: parseFloat(e.target.value) || null });
                }}
                onBlur={() => {
                  if (saveTimeoutRef.current) {
                    clearTimeout(saveTimeoutRef.current);
                    saveProductField({ totalProductsLeased: parseFloat(data.leasedAmount) || null });
                  }
                }}
                className="product-header-input-muted product-header-input-flex"
                disabled={isSaving}
              />
            </div>
          </div>
          <div>
            <Form.Label
              className="product-header-label-xs mb-1"
              style={{ marginLeft: "28px" }}
            >
              Take back
            </Form.Label>
            <div className="product-header-checkbox-input">
              <Form.Check
                type="checkbox"
                checked={data?.takebackChecked || false}
                onChange={(e) => {
                  setData({ ...data, takebackChecked: e.target.checked });
                  saveProductField({ takeBackCustomer: e.target.checked ? true : null });
                }}
                className="product-header-checkbox"
                disabled={isSaving}
              />
              <Form.Control
                type="number"
                placeholder="Mængde"
                value={data?.takebackAmount || ""}
                onChange={(e) => {
                  setData({ ...data, takebackAmount: e.target.value });
                  saveProductField({ totalProductsTakeback: parseFloat(e.target.value) || null });
                }}
                onBlur={() => {
                  if (saveTimeoutRef.current) {
                    clearTimeout(saveTimeoutRef.current);
                    saveProductField({ totalProductsTakeback: parseFloat(data.takebackAmount) || null });
                  }
                }}
                className="product-header-input-muted product-header-input-flex"
                disabled={isSaving}
              />
            </div>
          </div>
        </div>

        {/* Functional Unit Definition */}
        <div className="product-header-fu-grid">
          <div>
            <Form.Label className="product-header-label-xs-medium mb-2">
              Definer funktionel enhed
            </Form.Label>
            <div className="product-header-fu-inputs">
              <div style={{ width: "80px" }}>
                <Form.Label className="product-header-label-xs mb-1">
                  Amount
                </Form.Label>
                <Form.Control
                  type="number"
                  value={data?.fuAmount || "1"}
                  onChange={(e) => {
                    setData({ ...data, fuAmount: e.target.value });
                    saveProductField({ functionalUnitNum: parseFloat(e.target.value) || null });
                  }}
                  onBlur={() => {
                    if (saveTimeoutRef.current) {
                      clearTimeout(saveTimeoutRef.current);
                      saveProductField({ functionalUnitNum: parseFloat(data.fuAmount) || null });
                    }
                  }}
                  className="product-header-input-muted"
                  disabled={isSaving}
                />
              </div>
              <div style={{ width: "112px" }}>
                <Form.Label className="product-header-label-xs mb-1">
                  Unit
                </Form.Label>
                <Form.Select
                  value={data?.fuUnit || "pcs"}
                  onChange={(e) => {
                    setData({ ...data, fuUnit: e.target.value });
                    saveProductField({ functionalUnitUnit: e.target.value });
                  }}
                  className="product-header-input-muted"
                  disabled={isSaving}
                >
                  <option value="pcs">Pcs.</option>
                  <option value="kg">kg</option>
                  <option value="m2">m²</option>
                </Form.Select>
              </div>
            </div>
          </div>
          <div>
            <Form.Label className="product-header-label-xs mb-1">
              What is it?
            </Form.Label>
            <Form.Control
              name="functional-unit"
              autoComplete="off"
              value={data?.functionalUnit || ""}
              placeholder="F.eks. 1 flaske shampoo (250 ml), 1 par jeans, 1 m² bygning over 50 år"
              onChange={(e) => {
                setData({ ...data, functionalUnit: e.target.value });
                saveProductField({ functionalUnit: e.target.value, functionalUnitDesc: e.target.value });
              }}
              onBlur={() => {
                if (saveTimeoutRef.current) {
                  clearTimeout(saveTimeoutRef.current);
                  saveProductField({ functionalUnit: data.functionalUnit, functionalUnitDesc: data.functionalUnit });
                }
              }}
              className="product-header-input-muted"
              disabled={isSaving}
            />
          </div>
        </div>

        {/* Certificates Section */}
        <div className="product-header-certificates">
          {certifications.map((cert, index) => (
            <div key={index} className="product-header-cert-row">
              <div className="product-header-cert-field">
                <Form.Label className="product-header-label-xs mb-1-5">
                  Certificering
                </Form.Label>
                <Form.Select
                  value={cert.certificate}
                  onChange={(e) =>
                    updateCertification(index, "certificate", e.target.value)
                  }
                  className="product-header-input-muted"
                >
                  <option value="">Vælg...</option>
                  {certificateOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </div>

              <div
                className="product-header-cert-field"
                style={{ width: "120px" }}
              >
                <Form.Label className="product-header-label-xs mb-1-5">
                  Verified (Y/N)
                </Form.Label>
                <Form.Select
                  value={cert.verified}
                  onChange={(e) =>
                    updateCertification(index, "verified", e.target.value)
                  }
                  className="product-header-input-muted"
                >
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </Form.Select>
              </div>

              <div
                className="product-header-cert-field"
                style={{ width: "140px" }}
              >
                <Form.Label className="product-header-label-xs mb-1-5">
                  Date
                </Form.Label>
                <Form.Control
                  type="date"
                  value={cert.date}
                  onChange={(e) =>
                    updateCertification(index, "date", e.target.value)
                  }
                  className="product-header-input-muted"
                />
              </div>

              <div className="product-header-cert-field">
                <Form.Label className="product-header-label-xs mb-1-5">
                  Organization
                </Form.Label>
                <Form.Control
                  placeholder="Organization..."
                  value={cert.organization}
                  onChange={(e) =>
                    updateCertification(index, "organization", e.target.value)
                  }
                  className="product-header-input-muted"
                />
              </div>

              <div className="product-header-cert-field">
                <Form.Label className="product-header-label-xs mb-1-5">
                  UID
                </Form.Label>
                <Form.Control
                  placeholder="Unique ID..."
                  value={cert.uid}
                  onChange={(e) =>
                    updateCertification(index, "uid", e.target.value)
                  }
                  className="product-header-input-muted"
                />
              </div>

              <div className="product-header-cert-actions">
                {index === certifications.length - 1 ? (
                  <Button
                    type="button"
                    variant="outline-primary"
                    size="sm"
                    onClick={addCertification}
                    className="product-header-cert-btn-add"
                  >
                    <span>+</span>
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="outline-danger"
                    size="sm"
                    onClick={() => removeCertification(index)}
                    className="product-header-cert-btn-remove"
                  >
                    ×
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Product Identification Numbers */}
        <div className="product-header-ids">
          <h5 className="product-header-ids-title">
            <span>📋</span>
            Product identification numbers
          </h5>
          <div className="product-header-ids-row-3">
            <div>
              <Form.Label
                htmlFor="ccs-class"
                className="product-header-label-xs-medium mb-1-5"
              >
                CCS/CoClass/CCI
              </Form.Label>
              <Form.Control
                id="ccs-class"
                placeholder="Classification..."
                value={data?.ccsClassification || ""}
                onChange={(e) => {
                  setData({ ...data, ccsClassification: e.target.value });
                  saveProductField({ ccsClassification: e.target.value || null });
                }}
                onBlur={() => {
                  if (saveTimeoutRef.current) {
                    clearTimeout(saveTimeoutRef.current);
                    saveProductField({ ccsClassification: data.ccsClassification || null });
                  }
                }}
                className="product-header-input-bg"
                disabled={isSaving}
              />
            </div>

            <div>
              <Form.Label
                htmlFor="gtin-ean"
                className="product-header-label-xs-medium mb-1-5"
              >
                GTIN / EAN
              </Form.Label>
              <Form.Control
                id="gtin-ean"
                placeholder="GTIN/EAN..."
                value={data?.gtinEan || ""}
                onChange={(e) => {
                  setData({ ...data, gtinEan: e.target.value });
                  saveProductField({ gtinEan: e.target.value || null });
                }}
                onBlur={() => {
                  if (saveTimeoutRef.current) {
                    clearTimeout(saveTimeoutRef.current);
                    saveProductField({ gtinEan: data.gtinEan || null });
                  }
                }}
                className="product-header-input-bg"
                disabled={isSaving}
              />
            </div>

            <div>
              <Form.Label
                htmlFor="basta-number"
                className="product-header-label-xs-medium mb-1-5"
              >
                BASTA number
              </Form.Label>
              <Form.Control
                id="basta-number"
                placeholder="BASTA..."
                value={data?.bastaNumber || ""}
                onChange={(e) => {
                  setData({ ...data, bastaNumber: e.target.value });
                  saveProductField({ bastaNumber: e.target.value || null });
                }}
                onBlur={() => {
                  if (saveTimeoutRef.current) {
                    clearTimeout(saveTimeoutRef.current);
                    saveProductField({ bastaNumber: data.bastaNumber || null });
                  }
                }}
                className="product-header-input-bg"
                disabled={isSaving}
              />
            </div>
          </div>

          <div className="product-header-ids-row-2">
            <div>
              <Form.Label
                htmlFor="other-ids"
                className="product-header-label-xs-medium mb-1-5"
              >
                Other identification numbers
              </Form.Label>
              <Form.Control
                id="other-ids"
                placeholder="Other IDs..."
                value={data?.otherIds || ""}
                onChange={(e) => {
                  setData({ ...data, otherIds: e.target.value });
                  saveProductField({ otherIds: e.target.value || null });
                }}
                onBlur={() => {
                  if (saveTimeoutRef.current) {
                    clearTimeout(saveTimeoutRef.current);
                    saveProductField({ otherIds: data.otherIds || null });
                  }
                }}
                className="product-header-input-bg"
                disabled={isSaving}
              />
            </div>

            <div>
              <Form.Label
                htmlFor="created-modified"
                className="product-header-label-xs-medium mb-1-5"
              >
                Created/Modified (date)
              </Form.Label>
              <Form.Control
                id="created-modified"
                type="date"
                value={data?.createdModified || ""}
                onChange={(e) => {
                  setData({ ...data, createdModified: e.target.value });
                  saveProductField({ createdModified: e.target.value || null });
                }}
                onBlur={() => {
                  if (saveTimeoutRef.current) {
                    clearTimeout(saveTimeoutRef.current);
                    saveProductField({ createdModified: data.createdModified || null });
                  }
                }}
                className="product-header-input-bg"
                disabled={isSaving}
              />
            </div>
          </div>
        </div>

        {/* EPD Approval (Third-party verified) */}
        <EPDApprovalSection
          data={epdApprovalData}
          onChange={handleEpdApprovalChange}
          lcaLockStatus={lcaLockStatus}
          lcaVersionSubmitted={lcaVersionSubmitted}
        />
      </Card>
    </>
  );
};

export default ProductHeader;
